import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { NS, U3101ROLES } from "../../../U3101";
import {
  U3101_HOUR_BANK_TYPES,
  type U3101HourBankHistory,
  type U3101HourBankType,
  type U3101HourBankYear,
  type U3101HourBankYearStatus,
} from "../../../U3101Service";
import { useU3101HourBankHistoryHandlers } from "../../../U3101UseHourBankHistoryHandlers";

export interface U3101HourBankHistoryTabProps {
  employeeId: string;
  /** Après édition des initiales de l’année open (sync fiche `*Initial`). */
  onOpenYearInitialsChanged?: () => void;
}

function formatHours(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function bankTypeLabelKey(bankType: U3101HourBankType | string): string {
  switch (bankType) {
    case "vacation":
      return "lblHourBankTypeVacation";
    case "holidayLeave":
      return "lblHourBankTypeHolidayLeave";
    case "sickLeave":
      return "lblHourBankTypeSickLeave";
    case "special":
      return "lblHourBankTypeSpecial";
    default:
      return "lblHourBankTypeVacation";
  }
}

function statusLabelKey(status: U3101HourBankYearStatus): string {
  if (status === "closed") return "valHourBankYearClosed";
  if (status === "prepared") return "valHourBankYearPrepared";
  return "valHourBankYearOpen";
}

function statusBadgeClass(status: U3101HourBankYearStatus): string {
  if (status === "open") return "bg-emerald-100 text-emerald-800";
  if (status === "prepared") return "bg-sky-100 text-sky-800";
  return "bg-slate-200 text-slate-800";
}

function tileBorderClass(status: U3101HourBankYearStatus): string {
  if (status === "open") return "border-emerald-200 bg-emerald-50/30";
  if (status === "prepared") return "border-sky-200 bg-sky-50/30";
  return "border-gray-200 bg-white";
}

function buildInitialDraft(yearBlock: U3101HourBankYear): Record<U3101HourBankType, string> {
  const draft = {} as Record<U3101HourBankType, string>;
  for (const bankType of U3101_HOUR_BANK_TYPES) {
    const bank = yearBlock.banks.find((b) => b.bankType === bankType);
    draft[bankType] = String(bank?.initialHours ?? 0);
  }
  return draft;
}

function parseInitialDraft(
  draft: Record<U3101HourBankType, string>,
): { ok: true; banks: { bankType: U3101HourBankType; initialHours: number }[] } | { ok: false } {
  const banks: { bankType: U3101HourBankType; initialHours: number }[] = [];
  for (const bankType of U3101_HOUR_BANK_TYPES) {
    const raw = String(draft[bankType] ?? "").trim().replace(",", ".");
    const n = Number(raw);
    if (raw === "" || Number.isNaN(n) || n < 0) {
      return { ok: false };
    }
    banks.push({ bankType, initialHours: n });
  }
  return { ok: true, banks };
}

type YearTileProps = {
  yearBlock: U3101HourBankYear;
  canEdit: boolean;
  busy: boolean;
  onSave: (year: number, banks: { bankType: U3101HourBankType; initialHours: number }[]) => Promise<boolean>;
};

const U3101HourBankYearTile: React.FC<YearTileProps> = ({ yearBlock, canEdit, busy, onSave }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const editable = canEdit && yearBlock.status !== "closed";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => buildInitialDraft(yearBlock));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) {
      setDraft(buildInitialDraft(yearBlock));
    }
  }, [yearBlock, editing]);

  const startEdit = () => {
    setDraft(buildInitialDraft(yearBlock));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraft(buildInitialDraft(yearBlock));
    setEditing(false);
  };

  const saveEdit = async () => {
    const parsed = parseInitialDraft(draft);
    if (!parsed.ok) {
      toast.error(t("errHourBankInitialInvalid", { ns: NS }));
      return;
    }
    setSaving(true);
    const ok = await onSave(yearBlock.year, parsed.banks);
    setSaving(false);
    if (ok) {
      setEditing(false);
    }
  };

  return (
    <article className={`flex flex-col overflow-hidden rounded-lg border ${tileBorderClass(yearBlock.status)}`}>
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900">
            {t("lblHourBankHistoryYear", { ns: NS, year: yearBlock.year })}
          </h3>
          <span className={`shrink-0 rounded-sm px-2 py-0.5 text-xs font-medium ${statusBadgeClass(yearBlock.status)}`}>
            {t(statusLabelKey(yearBlock.status), { ns: NS })}
          </span>
        </div>
        {editable && !editing ? (
          <U2000ActionBtn
            label={t("btnEditHourBankYear", { ns: NS })}
            handleClick={startEdit}
            variant="secondary"
            disabled={busy || saving}
          />
        ) : null}
        {editable && editing ? (
          <div className="flex flex-wrap gap-2">
            <U2000ActionBtn
              label={t("btnCancel", { ns: NS })}
              handleClick={cancelEdit}
              variant="secondary"
              disabled={saving}
            />
            <U2000ActionBtn
              label={t("btnSaveHourBankYear", { ns: NS })}
              handleClick={() => void saveEdit()}
              disabled={saving || busy}
            />
          </div>
        ) : null}
      </header>

      <div className="overflow-x-auto px-1 pb-2 pt-1">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-wide text-gray-500">
              <th className="px-2 py-1.5 font-medium">{t("colHourBankType", { ns: NS })}</th>
              <th className="px-2 py-1.5 text-right font-medium tabular-nums">
                {t("colHourBankInitial", { ns: NS })}
              </th>
              <th className="px-2 py-1.5 text-right font-medium tabular-nums">
                {t("colHourBankBalance", { ns: NS })}
              </th>
              <th className="px-2 py-1.5 text-right font-medium tabular-nums">
                {t("colHourBankConsumed", { ns: NS })}
              </th>
            </tr>
          </thead>
          <tbody>
            {U3101_HOUR_BANK_TYPES.map((bankType) => {
              const bank = yearBlock.banks.find((b) => b.bankType === bankType);
              return (
                <tr key={`${yearBlock.year}-${bankType}`} className="border-t border-gray-100">
                  <td className="px-2 py-1.5 text-gray-700">{t(bankTypeLabelKey(bankType), { ns: NS })}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-900">
                    {editing ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        className="w-24 rounded-sm border border-gray-300 bg-white px-2 py-1 text-right text-sm tabular-nums focus:border-blue-500 focus:outline-hidden focus:ring-1 focus:ring-blue-500/30"
                        value={draft[bankType]}
                        disabled={saving}
                        onChange={(e) =>
                          setDraft((prev) => ({
                            ...prev,
                            [bankType]: e.target.value,
                          }))
                        }
                        aria-label={t(bankTypeLabelKey(bankType), { ns: NS })}
                      />
                    ) : (
                      formatHours(bank?.initialHours)
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-900">
                    {formatHours(bank?.closingBalanceHours)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-900">
                    {formatHours(bank?.consumedHours)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </article>
  );
};

const U3101HourBankHistoryTab: React.FC<U3101HourBankHistoryTabProps> = ({
  employeeId,
  onOpenYearInitialsChanged,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { hasRole } = useU2000AuthContext();
  const canEdit = hasRole(U3101ROLES.EDIT);
  const { fetch, cloneNext, updateYear } = useU3101HourBankHistoryHandlers();

  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<U3101HourBankHistory | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  const loadHistory = useCallback(async () => {
    const id = employeeId?.trim();
    if (!id) {
      setHistory(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadFailed(false);
    const result = await fetch(id);
    setHistory(result);
    setLoadFailed(result == null);
    setLoading(false);
  }, [employeeId, fetch]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const years = useMemo(() => {
    if (!history?.years?.length) return [] as U3101HourBankYear[];
    return [...history.years].sort((a, b) => b.year - a.year);
  }, [history]);

  const openYear = useMemo(() => years.find((y) => y.status === "open")?.year ?? null, [years]);

  const canPrepareNext = useMemo(() => {
    if (!canEdit || openYear == null) return false;
    const nextYear = openYear + 1;
    return !years.some((y) => y.year === nextYear);
  }, [canEdit, openYear, years]);

  const handleCloneNext = async () => {
    const id = employeeId?.trim();
    if (!id) return;
    setActionBusy(true);
    const result = await cloneNext(id);
    setActionBusy(false);
    if (result) {
      setHistory(result);
      toast.success(t("msgHourBankCloneNextSuccess", { ns: NS }));
    }
  };

  const handleSaveYear = async (
    year: number,
    banks: { bankType: U3101HourBankType; initialHours: number }[],
  ): Promise<boolean> => {
    const id = employeeId?.trim();
    if (!id) return false;
    setActionBusy(true);
    const result = await updateYear(id, year, { banks });
    setActionBusy(false);
    if (!result) return false;
    setHistory(result);
    toast.success(t("msgHourBankYearUpdateSuccess", { ns: NS }));
    if (openYear != null && year === openYear) {
      onOpenYearInitialsChanged?.();
    }
    return true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">{t("msgHourBankHistoryLoading", { ns: NS })}</p>
      </div>
    );
  }

  if (loadFailed || !history) {
    return (
      <div className="flex flex-col items-center gap-4 py-12">
        <p className="text-gray-600">{t("msgHourBankHistoryLoadError", { ns: NS })}</p>
        <U2000ActionBtn label={t("btnRefreshHourBankHistory", { ns: NS })} handleClick={() => void loadHistory()} />
      </div>
    );
  }

  return (
    <div className="space-y-3 py-2">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {canPrepareNext ? (
          <U2000ActionBtn
            label={
              openYear != null
                ? t("btnPrepareNextHourBankYear", { ns: NS, year: openYear + 1 })
                : t("btnPrepareNextHourBankYearGeneric", { ns: NS })
            }
            handleClick={() => void handleCloneNext()}
            disabled={actionBusy}
          />
        ) : null}
        <U2000ActionBtn
          label={t("btnRefreshHourBankHistory", { ns: NS })}
          handleClick={() => void loadHistory()}
          variant="secondary"
          disabled={actionBusy}
        />
      </div>

      {years.length === 0 ? (
        <div className="py-8 text-center text-sm text-gray-600">{t("msgHourBankHistoryEmpty", { ns: NS })}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {years.map((yearBlock) => (
            <U3101HourBankYearTile
              key={yearBlock.year}
              yearBlock={yearBlock}
              canEdit={canEdit}
              busy={actionBusy}
              onSave={handleSaveYear}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default U3101HourBankHistoryTab;
